import React, { useState } from 'react';
import capitalOneLogo from '../../images/capitalone.png';
import andurilLogo from '../../images/anduril.png';
import hytechLogo from '../../images/hytech.png';
import useScrollReveal from '../../hooks/useScrollReveal';

const workData = [
  {
    company: 'Anduril Industries',
    position: 'Software Engineer Intern',
    duration: 'May 2026 - Aug 2026',
    location: 'Seattle, WA',
    status: 'Current',
    description:
      'Summer 2026 as a Software Engineer Intern.',
    technologies: ['Systems', 'Go', 'Protobuf', 'Docker', 'Kubernetes'],
    logo: andurilLogo,
    detailedDescription:
      'Working on World Model - C2 Command and Control System in Lattice. Focusing on refining data pipelines system in Go and Protobuf.',
    achievements: [],
  },
  {
    company: 'HyTech Racing',
    position: 'Software and Firmware Engineer',
    duration: 'Sep. 2024 - Present',
    location: 'Atlanta, GA',
    status: 'Current',
    description:
      'Real-time pedal sensor firmware for Teensy 4.1, Kalman-filter BMS optimization, and high-throughput telemetry via TCP Ethernet.',
    technologies: ['C++', 'PlatformIO', 'Teensy 4.1', 'TCP/IP', 'Firmware'],
    logo: hytechLogo,
    detailedDescription:
      'Engineered real-time pedal sensor firmware for embedded Teensy 4.1 and optimized C++ BMS firmware with Kalman-filter models to maximize battery performance. Upgraded shutdown telemetry transmission with TCP Ethernet link and boosted sensor logging throughput by rewriting Python drivers in C++/PlatformIO.',
    achievements: [
      'Engineered real-time pedal sensor firmware on embedded Teensy 4.1',
      'Optimized C++ BMS firmware with Kalman-filter models for battery performance',
      'Upgraded shutdown telemetry over a TCP Ethernet link',
      'Rewrote Python sensor drivers in C++/PlatformIO for higher logging throughput',
    ],
  },
  {
    company: 'Capital One Financial Corporation',
    position: 'Software Engineer Intern',
    duration: 'Jun. 2025 - Aug. 2025',
    location: 'Dallas, TX',
    status: 'Past',
    description:
      'Engineered real-time event pipelines and automated critical security workflows to improve data processing efficiency and system integrity.',
    technologies: [
      'Python',
      'FastAPI',
      'SQL',
      'XML',
      'Beanshell',
      'Agile',
    ],
    logo: capitalOneLogo,
    detailedDescription:
      'Worked on high-impact projects focused on optimizing data processing workflows and automating critical security processes. Collaborated with cross-functional teams to deliver scalable solutions that significantly improved system performance and reliability.',
    achievements: [
      'Reduced end-to-end data processing time by ~70% by engineering a real-time event pipeline using a Python FastAPI microservice to replace a legacy enterprise process',
      'Designed a normalized 10-column SQL schema capable of processing 200,000+ transactions with 100% data integrity for a critical time-sensitive security workflow',
      'Automated a security workflow using XML and Beanshell scripting to parse company-critical database events and trigger appropriate actions',
      'Accelerated delivery of new automation by following the Agile framework and SDLC pipeline',
    ],
  },
];

const statusStyles = {
  Upcoming: 'text-accent border-accent',
  Current: 'text-signal border-signal',
  Past: 'text-ink-muted border-line-hi',
};

const TimelineEntry = ({ work, index, onOpen }) => {
  const ref = useScrollReveal();
  const direction = index % 2 === 0 ? 'reveal-left' : 'reveal-right';

  return (
    <div ref={ref} className={`${direction} relative`}>
      <div className="absolute left-0 md:left-1/2 md:-translate-x-1/2 top-8 flex items-center justify-center">
        <span className="w-2 h-2 bg-accent" />
      </div>

      <div
        onClick={() => onOpen(work)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') onOpen(work);
        }}
        className={`group relative ml-10 md:ml-0 md:w-1/2 cursor-pointer ${
          index % 2 === 0 ? 'md:pr-12' : 'md:ml-auto md:pl-12'
        }`}
      >
        <div className="border border-line bg-surface p-6 md:p-7 hover:border-accent transition-all duration-300 hover-lift">
          <div className="flex items-center justify-between mono-caption mb-4">
            <span>{work.duration}</span>
            <span
              className={`px-2 py-0.5 border text-[0.65rem] tracking-wider ${
                statusStyles[work.status] || 'text-ink-muted border-line'
              }`}
            >
              {work.status}
            </span>
          </div>

          <div className="flex items-start gap-4">
            {work.logo && (
              <div className="w-14 h-14 flex-shrink-0 border border-line bg-ink flex items-center justify-center p-1.5">
                <img
                  src={work.logo}
                  alt={`${work.company} logo`}
                  className="max-w-full max-h-full object-contain"
                />
              </div>
            )}
            <div className="min-w-0">
              <h3 className="font-display text-xl md:text-2xl text-ink group-hover:text-accent transition-colors">
                {work.position}
              </h3>
              <div className="mt-1 text-accent font-medium">{work.company}</div>
              <div className="mt-0.5 font-mono text-xs text-ink-dim">
                {work.location}
              </div>
            </div>
          </div>

          <p className="mt-5 text-ink-muted leading-relaxed text-[0.95rem]">
            {work.description}
          </p>

          {work.technologies?.length > 0 && (
            <div className="mt-5 flex flex-wrap gap-2">
              {work.technologies.map((t) => (
                <span
                  key={t}
                  className="font-mono text-[0.7rem] text-ink-muted border border-line px-2 py-0.5 hover:border-accent hover:text-accent transition-colors"
                >
                  {t}
                </span>
              ))}
            </div>
          )}

          <div className="mt-6 flex items-center justify-end font-mono text-xs text-ink-dim group-hover:text-accent transition-colors">
            <span>Details</span>
            <span className="ml-2">→</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const WorkHistory = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedWork, setSelectedWork] = useState(null);
  const headRef = useScrollReveal();

  const openModal = (work) => {
    setSelectedWork(work);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedWork(null);
  };

  return (
    <section
      id="work-history"
      className="relative bg-bg text-ink py-28 overflow-hidden"
    >
      <div className="relative container mx-auto px-6">
        <div ref={headRef} className="reveal max-w-4xl">
          <span className="eyebrow">03 / Experience</span>
          <h2 className="section-title mt-6 text-4xl sm:text-6xl md:text-7xl">
            Experience<span className="text-accent">.</span>
          </h2>
        </div>

        <div className="relative mt-20">
          <div className="hidden md:block absolute left-1/2 -translate-x-1/2 top-0 bottom-0 w-px bg-line">
            <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-bg to-transparent" />
            <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-bg to-transparent" />
          </div>
          <div className="md:hidden absolute left-1.5 top-0 bottom-0 w-px bg-line" />

          <div className="space-y-12">
            {workData.map((work, index) => (
              <TimelineEntry
                key={work.company + work.duration}
                work={work}
                index={index}
                onOpen={openModal}
              />
            ))}
          </div>
        </div>
      </div>

      {isModalOpen && selectedWork && (
        <div
          className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in"
          onClick={closeModal}
        >
          <div
            className="relative bg-surface border border-line max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 md:p-8">
              <div className="flex items-center justify-between mb-6">
                <span className="mono-caption">Role details</span>
                <button
                  onClick={closeModal}
                  className="text-ink-muted hover:text-accent text-2xl leading-none"
                  aria-label="Close"
                >
                  ×
                </button>
              </div>

              <div className="flex items-start gap-4">
                {selectedWork.logo && (
                  <div className="w-16 h-16 flex-shrink-0 border border-line bg-ink flex items-center justify-center p-1.5">
                    <img
                      src={selectedWork.logo}
                      alt={`${selectedWork.company} logo`}
                      className="max-w-full max-h-full object-contain"
                    />
                  </div>
                )}
                <div>
                  <h3 className="font-display text-2xl text-ink">
                    {selectedWork.position}
                  </h3>
                  <div className="text-accent font-medium mt-0.5">
                    {selectedWork.company}
                  </div>
                  <div className="font-mono text-xs text-ink-dim mt-1">
                    {selectedWork.duration} · {selectedWork.location}
                  </div>
                </div>
              </div>

              <div className="mt-6">
                <div className="mono-caption text-accent mb-2">Overview</div>
                <p className="text-ink-muted leading-relaxed">
                  {selectedWork.detailedDescription}
                </p>
              </div>

              {selectedWork.achievements?.length > 0 && (
                <div className="mt-6">
                  <div className="mono-caption text-accent mb-2">
                    Highlights
                  </div>
                  <ul className="space-y-2">
                    {selectedWork.achievements.map((a, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <span className="text-accent font-mono text-xs pt-1">
                          ▸
                        </span>
                        <span className="text-ink-muted leading-relaxed">
                          {a}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {selectedWork.technologies?.length > 0 && (
                <div className="mt-6">
                  <div className="mono-caption text-accent mb-2">
                    Technologies
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {selectedWork.technologies.map((t) => (
                      <span
                        key={t}
                        className="font-mono text-xs text-ink border border-line px-2.5 py-1"
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-8 flex justify-end">
                <button onClick={closeModal} className="btn-ghost">
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default WorkHistory;
